import React from 'react';
import './Sections.scss';
import Statistics2 from '../Statistics/Statistics2';
import Statistics3 from '../Statistics/Statistics3';
import Statistics4 from '../Statistics/Statistics4';

function Section3() {
  return (
    <section className="sections">
      <h2 className="sections-title">FOUR SUGGESTIONS FOR FIRST-TIME HOMEBUYERS</h2>
      <p className="sections-copy">
        Many aspiring first-time homebuyers may feel caught in a catch-22 situation. Having home
        equity would help them compete in today’s housing market, but they need to become homeowners
        to build that equity in the first place.
      </p>

      <p className="sections-copy">
        Fortunately, Lautz has four suggestions for novice homebuyers on ways to break through this
        conundrum by applying lessons learned from previous generations of homeowners:
      </p>
      <h3 className="sections-sub">1. Broaden your search</h3>

      <p className="sections-copy sections-copy--margin">
        First-time buyers who cannot find a suitable property that matches their budget and needs
        within their target search area may need to look further afield. In fact, many buyers are
        using this exact tactic and widening their geographic search parameters. Between 1989 and
        2021, the median distance between a homebuyer’s new home and former residence fluctuated
        between 10 and 15 miles. That number had been creeping up recently, but it more than tripled
        in 2022 hitting an all-time high of 50 miles.
      </p>
      <Statistics2 />
      <p className="sections-copy">
        But repeat buyers moved even farther than that. “The typical repeat buyer actually moved 90
        miles!” exclaims Lautz. “That’s a very long distance and yet another all-time record we had
        in our data this year.”
      </p>
      <p className="sections-copy">
        Obviously, some older repeat buyers who are retired may have more flexibility in terms of
        where they look for a new home. Younger first-time buyers in the early or middle stages of
        their career may feel they need to live within a reasonable commuting distance to their
        place of employment. However, the recent popularity of hybrid and remote work arrangements
        may give homebuyers of all ages more flexibility in terms of broadening their search to find
        areas that meet their budget or lifestyle preferences.
      </p>
      <div className="quote">
        <div className="quote__contents">
          <div className="quotationMark">“</div>
          <blockquote className="quote__copy">
            We know that people are holding onto their homes for longer periods of time...They are
            building their lives around these properties.”
          </blockquote>
          <div className="quote__caption">
            Dr. Jessica Lautz,{' '}
            <span className="italic">
              Deputy Chief Economist and Vice President of Research at NAR
            </span>
          </div>
        </div>
      </div>
      <h3 className="sections-sub">2. Settle in and stay awhile</h3>
      <p className="sections-copy">
        Home flipping – where a buyer scoops up a property, renovates it, and immediately puts it
        back on the market – might make for dramatic TV shows, but that’s not how today’s repeat
        homebuyers built their home equity.
      </p>
      <p className="sections-copy">
        In fact, NAR data shows that people who sold their homes in 2022 had owned those properties
        for a median of 10 years . “We know that people are holding onto their homes for longer
        periods of time,” says Lautz. “Perhaps they are remodeling those homes or they have a low
        interest rate. Either way, they are building equity and not thinking of it is a quick flip.
        They are building their lives around these properties and plan to hold onto them.”
      </p>
      <p className="sections-copy  sections-copy--margin">
        First-time homebuyers seem to be taking this message to heart. According to NAR data,
        first-time homebuyers who did manage to enter the market in 2022 plan to stay in their new
        homes for 18 years. That’s an all-time high and a huge departure from the expectations that
        first-time buyers had over the past decade.
      </p>
      <Statistics3 />
      <h3 className="sections-sub">3. Use the Friends and Family Plan</h3>
      <p className="sections-copy">
        Married couples still make up a majority (about 60%) of all homebuyers, but they are not the
        only demographic buying homes. Among first-time buyers in particular, 18 percent were
        unmarried couples, and five percent were other household compositions. In both cases, these
        are the highest percentages that NAR has recorded.
      </p>
      <p className="sections-copy">
        “Today, you don’t have to be part of a married couple with two kids to enter the housing
        market,” notes Lautz. “We’re seeing single women and single men enter, but we’re also seeing
        people saying they will enter [the housing market] with a roommate or perhaps even buy a
        home with a grandparent. I think the data really speaks to the fact that – only 50% of
        first-time home buyers are married, while the rest are finding different ways to enter into
        home ownership.”
      </p>
      <p className="sections-copy sections-copy--margin">
        Earlier generations certainly were accustomed to living together in multigenerational
        households. Whether or not first-time buyers want to team up with relatives to buy a home,
        they may be able to get a head start on homeownership by living with family before they buy.
        NAR data reveals that a record-high 27% of first-time homebuyers moved directly from a
        family member’s home into buying a home of their own.
      </p>
      <Statistics4 />
      <p className="sections-copy">
        In parts of the country where the housing supply crunch has been especially severe, local
        governments have embraced the idea of accessory dwelling units (ADUs) – small living units
        that can be built in constructed or converted spaces in a wide range of places including
        backyards, garages, and basements. “Where ADUs are legal, communities are absolutely
        embracing the concept,” says Lautz. “These ADUs can help facilitate multigenerational
        housing by providing a place for an elderly parent or a boomerang young adult. This goes
        back to an older idea of having families stick together that was widespread in earlier
        generations. ADUs can not only address the housing shortage, they can also be used as rental
        units, providing an income stream that helps first-time homeowners pay down their
        mortgages.”
      </p>
    </section>
  );
}

export default Section3;
